import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, UserPlus } from 'lucide-react';

const roleBadgeStyles = {
  owner:     { backgroundColor: '#f3e8ff', color: '#7c3aed' },
  editor:    { backgroundColor: '#dbeafe', color: '#2563eb' },
  commenter: { backgroundColor: '#d1fae5', color: '#065f46' },
  viewer:    { backgroundColor: '#f3f4f6', color: '#6b7280' },
};

const SHARE_ROLES = ['editor', 'viewer'];

function SheetShareModal({ sheet, currentUserId, onClose }) {
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [sharing, setSharing] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [error, setError] = useState('');
  const [useIdFallback, setUseIdFallback] = useState(false);
  const [fallbackUserId, setFallbackUserId] = useState('');

  const loadPermissions = async () => {
    try {
      const res = await axios.get(`/api/sheets/${sheet.id}/permissions`);
      setPermissions(res.data || []);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    } finally {
      setLoadingPerms(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get('/api/users/list');
      setUsers(res.data || []);
    } catch (err) {
      // Fall back to user ID input if endpoint not available
      setUseIdFallback(true);
    }
  };

  useEffect(() => {
    loadPermissions();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet.id]);

  const addedUserIds = new Set(permissions.map(p => p.user_id));
  const availableUsers = users.filter(u => !addedUserIds.has(u.id));

  const handleRemove = async (permId) => {
    if (!window.confirm("Are you sure you want to revoke this user's access?")) return;
    try {
      await axios.delete(`/api/sheets/${sheet.id}/permissions/${permId}`);
      setPermissions(prev => prev.filter(p => p.id !== permId));
    } catch (err) {
      setError('Failed to remove permission.');
    }
  };

  const handleShare = async () => {
    const userId = useIdFallback ? Number(fallbackUserId) : Number(selectedUserId);
    if (!userId) {
      setError('Please select a user.');
      return;
    }
    setSharing(true);
    setError('');
    try {
      await axios.post(`/api/sheets/${sheet.id}/share`, { user_id: userId, role: selectedRole });
      await loadPermissions();
      setSelectedUserId('');
      setFallbackUserId('');
      setSelectedRole('viewer');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to share sheet.');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          width: '480px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
            Share {sheet.name}
          </h3>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Permissions list */}
        {loadingPerms ? (
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading...</p>
        ) : permissions.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>No permissions yet.</p>
        ) : (
          <div>
            {permissions.map(perm => {
              const isOwnOwner = perm.user_id === currentUserId && perm.role === 'owner';
              const badgeStyle = roleBadgeStyles[perm.role] || roleBadgeStyles.viewer;
              return (
                <div
                  key={perm.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #f3f4f6',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px', color: '#111827' }}>
                      {perm.user_name || perm.name || `User #${perm.user_id}`}
                    </div>
                    {(perm.user_email || perm.email) && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>
                        {perm.user_email || perm.email}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: 500,
                      ...badgeStyle,
                    }}>
                      {perm.role}
                    </span>
                    {!isOwnOwner && (
                      <button
                        onClick={() => handleRemove(perm.id)}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#d1d5db',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#d1d5db'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add people section */}
        <div style={{
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
            <UserPlus size={14} color="#7c3aed" />
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Add people</span>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {useIdFallback ? (
              <input
                type="number"
                value={fallbackUserId}
                onChange={(e) => setFallbackUserId(e.target.value)}
                placeholder="User ID"
                style={{
                  flex: 1,
                  minWidth: '80px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '13px',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            ) : (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '13px',
                  outline: 'none',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                }}
                onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select a person...</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} {u.name && u.email ? `(${u.email})` : ''}
                  </option>
                ))}
              </select>
            )}

            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '13px',
                outline: 'none',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
              }}
              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              {SHARE_ROLES.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>

            <button
              onClick={handleShare}
              disabled={sharing}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: sharing ? '#d1d5db' : '#7c3aed',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 500,
                cursor: sharing ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {sharing ? 'Sharing...' : 'Share'}
            </button>
          </div>

          {error && (
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SheetShareModal;
